import { useEffect, useLayoutEffect, useRef, useState, type ChangeEvent, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import { PersistencePanel } from '../../components/panels/PersistencePanel';
import { MarkushImportPanel } from '../../components/panels/MarkushImportPanel';
import { SmilesImportPanel } from '../../components/panels/SmilesImportPanel';
import { PubChemImportPanel } from '../../components/panels/PubChemImportPanel';
import { OcsrImportPanel } from '../../components/panels/OcsrImportPanel';
import { FileFormatExport, FileFormatImport } from './FileFormatPanel';
import type { useDrawingPersistence } from '../../hooks/useDrawingPersistence';
import type { useSmilesImport } from '../../hooks/useSmilesImport';
import type { usePubChemImport } from '../../hooks/usePubChemImport';
import type { useOcsrImport } from '../../hooks/useOcsrImport';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useOutsideLookupsAllowed } from '../../tenancy/activeTenant';

export type ImportTarget = 'json' | 'smiles' | 'markush' | 'pubchem' | 'image';

interface FileDrawerProps {
  readonly importTarget?: ImportTarget | null;
  /** Saves what is on the paper: a molecule drawing, or -- with a reaction arrow -- a reaction (#705). */
  readonly onSave: () => void;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly persistence: ReturnType<typeof useDrawingPersistence>;
  readonly smilesImport: ReturnType<typeof useSmilesImport>;
  readonly pubChemImport: ReturnType<typeof usePubChemImport>;
  readonly ocsrImport: ReturnType<typeof useOcsrImport>;
  readonly onNewDrawing: () => void;
  readonly onImportJson: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onExportJson: () => void;
  readonly onExportSvg: () => void;
  readonly onPrint?: () => void;
  readonly jsonImportError: string | null;
  // #648 -- a Markush drawing can't be saved to the server yet (#634 owns persistence); Save is
  // replaced by a pointer to JSON export, which does produce the Markush document.
  readonly markushDrawing?: boolean;
  // #663 -- opens a CXSMILES / molfile / RGfile in Markush mode; see MarkushImportPanel.
  readonly onImportMarkush?: (text: string) => Promise<readonly string[] | null>;
}

// ADR 0007 P8 — save / import / export for the workspace shell, in a drawer opened from the File
// control. The drawer traps focus while open; WorkspaceShell returns focus to the trigger on
// close. #837 (owner, #836) -- three groups, Save / Import / Export; no "load by ID" and no Recent
// drawings list (drawings open from My drawings and search results; archived ones are the My
// drawings feed's Archived view, in the Filter tab).
export function FileDrawer({
  open,
  importTarget = null,
  onClose,
  onSave,
  persistence,
  smilesImport,
  pubChemImport,
  ocsrImport,
  onNewDrawing,
  onImportJson,
  onExportJson,
  onExportSvg,
  onPrint,
  jsonImportError,
  markushDrawing = false,
  onImportMarkush
}: FileDrawerProps) {
  // #1064: PubChem and image import send things to outside services -- not offered where the
  // organisation has turned outside lookups off.
  const outsideLookupsAllowed = useOutsideLookupsAllowed();
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const title = importTarget ? t('workspace.actionBar.import') : t('workspace.fileDrawer.title');
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    if (importTarget === 'smiles') return;
    closeRef.current?.focus();
  }, [open, importTarget]);

  // #837 (owner, #836) -- the drawer opens below the button that opened it: the folder button, or
  // ⋯ when File has moved into it (#835). It stays on screen, its body scrolling. Below 600 px it
  // is a full-width sheet as before.
  const [anchoredStyle, setAnchoredStyle] = useState<CSSProperties | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    const launchTrigger = active instanceof HTMLElement && active.closest('.workspace-toolbar') ? active : null;
    const place = () => {
      if (window.innerWidth < 600) {
        setAnchoredStyle(null);
        return;
      }
      const visible = (element: Element | null) =>
        element instanceof HTMLElement && element.offsetParent !== null ? element : null;
      const anchor =
        visible(launchTrigger) ??
        visible(document.querySelector('[data-testid="file-drawer-trigger"]')) ??
        visible(document.querySelector('[data-testid="more-menu-trigger"]'));
      if (!anchor) {
        setAnchoredStyle(null);
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const margin = 8;
      const width = Math.min(420, window.innerWidth - 2 * margin);
      const top = rect.bottom + 6;
      setAnchoredStyle({
        top,
        left: Math.min(Math.max(margin, rect.left), window.innerWidth - width - margin),
        width,
        maxHeight: window.innerHeight - top - margin
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEscapeKey(open, onClose);

  useFocusTrap(open, dialogRef);

  if (!open) return null;

  return (
    <div
      className={`workspace-drawer-backdrop${anchoredStyle ? ' workspace-drawer-backdrop--anchored' : ''}`}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`workspace-drawer${anchoredStyle ? ' workspace-drawer--anchored' : ''}`}
        style={anchoredStyle ?? undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="file-drawer"
      >
        <div className="workspace-drawer-head">
          <h2>{title}</h2>
          <button
            ref={closeRef}
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label={t('workspace.fileDrawer.close')}
            title={t('workspace.fileDrawer.close')}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="workspace-drawer-body">
          {!importTarget ? (
            <div className="file-drawer-group" role="group" aria-labelledby="file-drawer-save">
              <div id="file-drawer-save" className="file-drawer-group-label">
                {t('workspace.fileDrawer.groups.save')}
              </div>
              {markushDrawing ? (
                <p className="workspace-markush-save-note" data-testid="markush-save-note">
                  {t('workspace.fileDrawer.markushNotSaved')}
                </p>
              ) : (
                <PersistencePanel persistence={persistence} onSave={onSave} onNewDrawing={onNewDrawing} />
              )}
            </div>
          ) : null}
          <div className="file-drawer-group" role="group" aria-labelledby="file-drawer-import">
            <div id="file-drawer-import" className="file-drawer-group-label">
              {t('workspace.fileDrawer.groups.import')}
            </div>
            {!importTarget || importTarget === 'smiles' ? (
              <SmilesImportPanel
                smilesImport={smilesImport}
                autoFocus={importTarget === 'smiles'}
                onSuccess={onClose}
              />
            ) : null}
            {onImportMarkush && (!importTarget || importTarget === 'markush') ? (
              <MarkushImportPanel onImport={onImportMarkush} />
            ) : null}
            {outsideLookupsAllowed && (!importTarget || importTarget === 'pubchem') ? (
              <PubChemImportPanel pubChemImport={pubChemImport} />
            ) : null}
            {outsideLookupsAllowed && (!importTarget || importTarget === 'image') ? (
              <OcsrImportPanel ocsrImport={ocsrImport} />
            ) : null}
            {!importTarget || importTarget === 'json' ? (
              <FileFormatImport onImportJson={onImportJson} importError={jsonImportError} />
            ) : null}
          </div>
          {!importTarget ? (
            <div className="file-drawer-group" role="group" aria-labelledby="file-drawer-export">
              <div id="file-drawer-export" className="file-drawer-group-label">
                {t('workspace.fileDrawer.groups.export')}
              </div>
              <FileFormatExport onExportJson={onExportJson} onExportSvg={onExportSvg} onPrint={onPrint} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
